import { EventEmitter, inject, Injectable } from '@angular/core';
import { DpModalService, HeaderAction } from '@docpath/webcomponents';
import { faMagnifyingGlass, faPlusCircle } from '@fortawesome/free-solid-svg-icons';

import { Alias } from '../../../alias/models/alias';
import { AliasHttpService } from '../../../alias/services/alias-http.service';
import { AlertRule } from '../../models/alert-rule';
import { AlertRulesService } from '../../services/alert-rules.service';
import { AlertRulesFormService } from '../../services/alert-rules-form.service';
import { AlertRulesHttpService } from '../../services/alert-rules-http.service';

@Injectable({
    providedIn: 'root'
})
export class AlertRulesHeaderService {

    aliasOptionsLoaded: EventEmitter<Alias[]> = new EventEmitter();
    private readonly i18nPrefix = 'alert-rules-config';

    private readonly alertRulesFormsService = inject(AlertRulesFormService);
    private readonly aliasHttpService = inject(AliasHttpService);
    private readonly alertRulesHttpService = inject(AlertRulesHttpService);
    private readonly modalService = inject(DpModalService);
    private readonly alertRulesService = inject(AlertRulesService);

    getRightActions(alias: Alias[]): HeaderAction[] {
        return [
            this.initNewAlertRuleAction(alias),
            this.initSearchAction()
        ];
    }

    getMoreActions(selectedAlertRules: AlertRule[]): HeaderAction[] {

        const actions = [];

        if (selectedAlertRules.length > 0) {
            actions.push(this.initDeleteAction(selectedAlertRules));
        }

        return actions;

    }

    getLeftActions(selectedAlertRules: AlertRule[], alias: Alias[]): HeaderAction[] {

        const actions = [];

        if (selectedAlertRules.length === 1) {
            const alertRuleSelected = selectedAlertRules[0];
            actions.push(this.initEditAction(alertRuleSelected, alias));
        }

        return actions;

    }

    private initSearchAction(): HeaderAction {

        const action = new HeaderAction(`${this.i18nPrefix}.header.search.label`, { cssClass: 'btn btn-dpsecondary' });
        action.tooltip = `${this.i18nPrefix}.header.search.tooltip`;
        action.icon = faMagnifyingGlass;

        return action;

    }

    private initNewAlertRuleAction(alias: Alias[]): HeaderAction {

        const alertRuleNewAction = new HeaderAction(`${this.i18nPrefix}.header.new-alert-rule.label`);

        alertRuleNewAction.icon = faPlusCircle;
        alertRuleNewAction.tooltip = `${this.i18nPrefix}.header.new-alert-rule.tooltip`;
        alertRuleNewAction.action = () => this.alertRulesFormsService.openCreationAlertRuleForm(alias);

        return alertRuleNewAction;

    }

    private initDeleteAction(alertRules: AlertRule[]): HeaderAction {
        const action = new HeaderAction(`${this.i18nPrefix}.header.delete.label`);

        action.tooltip = `${this.i18nPrefix}.header.delete.tooltip`;
        action.action = () => this.deleteAlertRules(alertRules);

        return action;
    }

    getSearchAction(actions: HeaderAction[]): HeaderAction {
        return actions[1];
    }

    loadAliasOptions(): void {

        this.aliasHttpService.getAllAlias(result => {
            this.aliasOptionsLoaded.emit(result);
        });
    }

    private deleteAlertRules(alertRules: AlertRule[]): void {

        const alertRulesIds = alertRules.map(alertRuleElement => alertRuleElement.id),
            modal = this.modalService.openQuestion(`${this.i18nPrefix}.modal.delete-question`);

        modal.componentInstance.accepted.subscribe(() => {
            this.deleteAlertRuleRequest(alertRulesIds);
        });

    }

    private deleteAlertRuleRequest(alertRulesIds: number[]): void {

        this.alertRulesHttpService.deleteAlertRules(alertRulesIds, () => {
            this.alertRulesService.notifyAlertRuleChange();
        });

    }

    private initEditAction(alertRule: AlertRule, alias: Alias[]): HeaderAction {

        const action = new HeaderAction(`${this.i18nPrefix}.header.edit.label`);

        action.tooltip = `${this.i18nPrefix}.header.edit.tooltip`;
        action.action = () => this.alertRulesFormsService.openEditAlertRuleForm(alertRule, alias);

        return action;

    }

}